import React from "react";
import { DISMISS, Input, useNotification } from "collections";
import { useTranslation } from "utilities/i18n";
import { LoadingIndicator, PolyIcon } from "elements";
import { FieldArray, FormikErrors, useField, useFormikContext } from "formik";
import "./NotificationCenter.scss";
import {
  useAddRecipientMutation,
  useRemoveRecipientMutation,
  AddRecipientMutation
} from "generated/graphql";

const EmailAddressField: React.FC<{
  isEditable?: boolean;
  remove<T>(index: number): T | undefined;
  index: number;
  isValid: boolean;
  recipient: { id: string; email: string; confirmed: boolean };
  errors: FormikErrors<{
    recipients: { id: string; email: string }[];
  }>;
  replaceEmail: (
    index: number,
    value: AddRecipientMutation["ncAddRecipient"]
  ) => void;
  initialValue: {
    id: string;
    confirmed: boolean;
    email: string;
  };
  audienceId: string;
}> = ({
  isEditable = true,
  remove, // now this is the ArrayHelpers.remove
  index,
  recipient,
  isValid,
  replaceEmail,
  audienceId,
  initialValue
}) => {
  const { t } = useTranslation();
  const [, setAddRecipient] = useAddRecipientMutation();
  const [, setRemoveRecipient] = useRemoveRecipientMutation();
  const [addNotification] = useNotification();
  const [{ value }, { touched }] = useField(`recipients[${index}].email`);
  const { errors } = useFormikContext<{ recipients: string[] }>();
  const edited = value !== initialValue.email;

  async function removeEmail() {
    if (audienceId !== "" && recipient.id) {
      const response = await setRemoveRecipient({
        audienceId,
        recipientId: recipient.id
      });
      if (response.data?.ncRemoveRecipient) {
        addNotification({
          type: "success",
          message: t("NOTIFICATION_CENTER_EMAIL_REMOVAL_SUCCESS", {
            email: recipient.email
          }),
          dismiss: DISMISS.FAST
        });
      }
      remove(index);
    }
  }

  async function handleUpdateEmail() {
    const canSubmit =
      recipient.email !== "" &&
      isValid &&
      initialValue.email !== recipient.email &&
      !!recipient.id &&
      audienceId !== "";

    if (canSubmit) {
      // Without an update mutation, we need to remove the existing entry and add a new one
      const removeResponse = await setRemoveRecipient({
        audienceId,
        recipientId: recipient.id
      });
      const addResponse = await setAddRecipient({
        audienceId,
        email: recipient.email
      });
      if (!removeResponse.error && !addResponse.error && addResponse.data) {
        replaceEmail(index, addResponse.data.ncAddRecipient); // Replace the entry in the field array
        addNotification({
          type: "success",
          message: t("NOTIFICATION_CENTER_EMAIL_ADD_SUCCESS", {
            email: recipient.email
          }),
          dismiss: DISMISS.FAST
        });
        return true;
      }
      //error handle
      else return false;
    }
  }

  function handleKeyPress({ key }: React.KeyboardEvent<HTMLInputElement>) {
    if (key === "Enter") {
      handleUpdateEmail();
    }
  }

  return (
    <div className="form-group">
      <label className="col-3 text-right" htmlFor={"email-input"}>
        {t("EMAIL_ADDRESS")}:
      </label>
      <div className="col-3">
        <div>
          <Input
            placeholder="name@company.com"
            name={`recipients[${index}].email`}
            disabled={!isEditable}
            autoComplete="new-password"
            onKeyUp={e => handleKeyPress(e)}
          />
          {errors.recipients?.length && touched ? (
            <div className="has-error">
              <p className="error-message">{errors.recipients[index]}</p>
            </div>
          ) : null}
        </div>
      </div>
      <div className="col-3 pl-0">
        {recipient.email && !edited ? (
          <button
            type="button"
            className={`btn-poly-icon remove-email-recipient`}
            data-testid={`remove-email[${index}]`}
            onClick={() => {
              removeEmail();
            }}
          >
            <PolyIcon icon="clear_x" />
          </button>
        ) : recipient.email && edited ? (
          <button
            type="button"
            className="update-link font-sm"
            data-testid="update-link"
            onClick={() => {
              handleUpdateEmail();
            }}
          >
            {t("UPDATE")}
          </button>
        ) : (
          <></>
        )}
        {recipient.confirmed && (
          <div className="verified-email col-3">
            <PolyIcon
              icon="check_circle_outline"
              className="verified-icon mr-5"
            />
            <div className="verified-label">{t("VERIFIED")}</div>
          </div>
        )}
      </div>
    </div>
  );
};

const NewEmailField: React.FC<{
  addToRecipients: (obj: AddRecipientMutation["ncAddRecipient"]) => void;
  audienceId: string;
}> = ({ addToRecipients, audienceId }) => {
  const { t } = useTranslation();
  const [, setAddRecipient] = useAddRecipientMutation();
  const [addNotification] = useNotification();
  const [{ value }, { error, initialValue }, { setValue }] = useField(
    "newRecipient"
  );

  const edited = value !== initialValue;

  function handleKeyPress({ key }: React.KeyboardEvent<HTMLInputElement>) {
    if (key === "Enter" && edited && !error) {
      addEmail(value);
    }
  }

  async function addEmail(email: string) {
    if (email !== "" && audienceId !== "") {
      const response = await setAddRecipient({ audienceId, email });
      if (!response.error && response.data) {
        addNotification({
          type: "success",
          message: t("NOTIFICATION_CENTER_EMAIL_ADD_SUCCESS", {
            email
          }),
          dismiss: DISMISS.FAST
        });
        addToRecipients({
          __typename: "Recipient",
          id: response.data.ncAddRecipient.id,
          email: response.data.ncAddRecipient.email,
          confirmed: response.data.ncAddRecipient.confirmed
        });
        setValue("");
      }
    }
  }

  const showAdd = !error && value !== "";

  return (
    <div className="form-group">
      <label className="col-3 text-right" htmlFor={"email-input"}>
        {t("EMAIL_ADDRESS")}:
      </label>
      <div className="col-3">
        <div>
          <Input
            placeholder="name@company.com"
            data-testid="new-recipient-input"
            name="newRecipient"
            autoComplete="new-password"
            onKeyUp={e => handleKeyPress(e)}
          />
        </div>
      </div>
      <div className="col-3 pl-0">
        {showAdd && (
          <button
            type="button"
            className="add-link font-sm"
            data-testid="add-link"
            onClick={() => {
              addEmail(value);
            }}
          >
            {t("ADD")}
          </button>
        )}
      </div>
    </div>
  );
};

const EmailList: React.FC<{
  values: {
    recipients: {
      id: string;
      confirmed: boolean;
      email: string;
    }[];
  };
  loading: boolean;
  isValid: boolean;
  audienceId: string;
  initialValues: {
    recipients: {
      id: string;
      confirmed: boolean;
      email: string;
    }[];
  };
  errors: FormikErrors<{
    recipients: {
      id: string;
      email: string;
    }[];
  }>;
}> = ({ values, loading, isValid, audienceId, errors, initialValues }) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="mt-15 ph-2">
        <label>{t("RECIPIENTS")}</label>
      </div>
      <div className="mt-5 mb-15 ph-2">
        <p className="font-sm">{t("NOTIFICATION_CENTER_RECIPIENTS_INFO")}</p>
      </div>
      {loading ? (
        <LoadingIndicator className="inline text-center" title="" />
      ) : (
        <FieldArray name="recipients">
          {arrayHelpers => (
            <>
              {values.recipients.length > 0 ? (
                values.recipients.map((recipient, index) => {
                  return (
                    <EmailAddressField
                      key={index}
                      audienceId={audienceId}
                      remove={arrayHelpers.remove}
                      initialValue={
                        initialValues.recipients.filter(
                          r => r.id === recipient.id
                        )[0]
                      }
                      replaceEmail={arrayHelpers.replace}
                      index={index}
                      isValid={isValid}
                      recipient={recipient}
                      errors={errors}
                    />
                  );
                })
              ) : (
                <></>
              )}
              <NewEmailField
                addToRecipients={arrayHelpers.push}
                audienceId={audienceId}
              />
            </>
          )}
        </FieldArray>
      )}
    </>
  );
};

export default EmailList;
